/**
 * useApiFetch.ts — TanStack Query 기반 API 훅
 *
 * 기존 useApiFetch / useApiMutation 인터페이스를 유지하되
 * 내부를 TanStack Query로 교체하여 캐시·중복 제거·자동 무효화 확보.
 */

import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiQueryFn } from '../lib/queryClient.js'
import { apiFetch, getErrorMessage } from '../lib/api.js'

// ---------------------------------------------------------------------------
// useApiFetch
// ---------------------------------------------------------------------------

interface UseApiFetchResult<T> {
  data: T | null
  error: string | null
  isLoading: boolean
  /** 수동으로 재요청 트리거 */
  refetch: () => void
  /** 낙관적 업데이트 — 로컬 데이터를 즉시 교체 */
  mutate: (data: T) => void
}

interface UseApiFetchOptions extends Omit<RequestInit, 'signal'> {
  skipAuth?: boolean
}

/**
 * GET 자동 fetch 훅.
 * path가 null이면 fetch를 건너뜀 (조건부 fetch).
 */
export function useApiFetch<T = unknown>(
  path: string | null,
  opts: UseApiFetchOptions = {},
): UseApiFetchResult<T> {
  const qc = useQueryClient()

  const { data, error, isLoading, refetch: tanstackRefetch } = useQuery<T, Error>({
    queryKey: path ? [path] : ['__disabled__'],
    queryFn: () => apiQueryFn<T>(path!, opts),
    enabled: path !== null,
  })

  const refetch = useCallback(() => {
    tanstackRefetch()
  }, [tanstackRefetch])

  const mutate = useCallback(
    (newData: T) => {
      if (path) {
        qc.setQueryData<T>([path], newData)
      }
    },
    [qc, path],
  )

  return {
    data: data ?? null,
    error: error?.message ?? null,
    isLoading,
    refetch,
    mutate,
  }
}

// ---------------------------------------------------------------------------
// useApiMutation
// ---------------------------------------------------------------------------

export interface TriggerOptions {
  /** 동적 경로 오버라이드 (예: `/api/ai/generations/${id}`) */
  pathOverride?: string
}

interface UseApiMutationResult<T, B> {
  /**
   * mutation 트리거.
   * 성공 시 data를 반환하고, 실패 시 null을 반환한다.
   */
  trigger: (body?: B, opts?: TriggerOptions) => Promise<T | null>
  data: T | null
  error: string | null
  isLoading: boolean
  /** data와 error를 null로 초기화 */
  reset: () => void
}

type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface OptimisticUpdateConfig<T> {
  /** 낙관적 업데이트 시 관련 쿼리 키 */
  queryKey: string[]
  /** 캐시를 낙관적으로 갱신할 함수 */
  updater: (old: T) => T
}

export interface UseApiMutationOptions<T> {
  /** 성공 시 자동으로 무효화할 쿼리 키 목록 */
  invalidateKeys?: string[][]
  /** 낙관적 업데이트 함수 — body를 받아 queryKey + updater 반환 */
  onMutate?: (body: unknown) => OptimisticUpdateConfig<T>
}

/**
 * POST/PUT/DELETE 수동 트리거 훅.
 */
export function useApiMutation<T = unknown, B = unknown>(
  path: string,
  method: HttpMethod = 'POST',
  options: UseApiMutationOptions<T> = {},
): UseApiMutationResult<T, B> {
  const qc = useQueryClient()

  const mutation = useMutation<
    T | null,
    Error,
    { body?: B; triggerOpts?: TriggerOptions },
    { queryKey?: string[]; previous?: T }
  >({
    mutationFn: async ({ body, triggerOpts }) => {
      const targetPath = triggerOpts?.pathOverride ?? path
      const fetchOptions: RequestInit & { skipAuth?: boolean } = { method }
      if (body !== undefined) {
        fetchOptions.body = JSON.stringify(body)
      }
      const res = await apiFetch<T>(targetPath, fetchOptions)
      if (res.success) return res.data
      throw new Error(getErrorMessage(res.error))
    },

    onMutate: async ({ body }) => {
      if (!options.onMutate || !body) return {}

      const config = options.onMutate(body)
      // 진행 중인 쿼리 취소
      await qc.cancelQueries({ queryKey: config.queryKey })
      const previous = qc.getQueryData<T>(config.queryKey)
      // 낙관적 업데이트 적용
      qc.setQueryData<T>(config.queryKey, (old) =>
        old ? config.updater(old) : old,
      )
      return { queryKey: config.queryKey, previous }
    },

    onError: (_err, _vars, context) => {
      // 실패 시 롤백
      if (context?.queryKey && context.previous !== undefined) {
        qc.setQueryData(context.queryKey, context.previous)
      }
    },

    onSettled: () => {
      // 성공/실패 모두 지정된 키 무효화
      if (options.invalidateKeys) {
        for (const key of options.invalidateKeys) {
          qc.invalidateQueries({ queryKey: key })
        }
      }
    },
  })

  const trigger = useCallback(
    async (body?: B, opts: TriggerOptions = {}): Promise<T | null> => {
      try {
        const result = await mutation.mutateAsync({
          body,
          triggerOpts: opts,
        })
        return result
      } catch {
        return null
      }
    },
    [mutation.mutateAsync],
  )

  return {
    trigger,
    data: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    isLoading: mutation.isPending,
    reset: mutation.reset,
  }
}
